import React, { useState, useEffect } from 'react';
import { useToastContext } from '@/contexts/ToastContext';
import { Eye, EyeOff, Lock, Save, Settings } from 'lucide-react';
import { useAppStore } from '@/store';
import { LoadingSpinner } from '@/components/LoadingSpinner';

interface UserSettings {
  account: {
    username: string;
    email: string;
  };
}

export const SettingsPage: React.FC = () => {
  const { user } = useAppStore();
  const { showSuccess, showError } = useToastContext();
  const [settings, setSettings] = useState<UserSettings>({
    account: {
      username: user?.username || '',
      email: user?.email || '',
    },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  useEffect(() => {
    const fetchUserSettings = async () => {
      try {
        const response = await fetch('/api/user/settings/');
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
        } else {
          setSettings({
            account: {
              username: user?.username || '',
              email: user?.email || '',
            },
          });
        }
      } catch (error) {
        console.error('Failed to fetch user settings:', error);
        setSettings({
          account: {
            username: user?.username || '',
            email: user?.email || '',
          },
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserSettings();
  }, [user]);

  const handleSaveSettings = async () => {
    try {
      const response = await fetch('/api/user/settings/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        showSuccess('Settings saved successfully!');
      } else {
        showError('Failed to save settings. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      showError('Failed to save settings. Please try again.');
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.new !== passwordData.confirm) {
      showError('New passwords do not match!');
      return;
    }

    if (passwordData.new.length < 8) {
      showError('Password must be at least 8 characters long!');
      return;
    }

    try {
      const response = await fetch('/api/user/change-password/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          current_password: passwordData.current,
          new_password: passwordData.new,
        }),
      });

      if (response.ok) {
        showSuccess('Password changed successfully!');
        setPasswordData({ current: '', new: '', confirm: '' });
      } else {
        showError('Failed to change password. Please check your current password.');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      showError('Failed to change password. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center p-4">
        <LoadingSpinner size="lg" color="primary" message="Loading settings..." />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl p-4 lg:p-6">
      <div className="mb-6 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 text-gray-700">
            <Settings className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
            <p className="text-sm text-gray-600">Manage your account security and sign-in details.</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-700">
              <Lock className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Security</h2>
              <p className="text-sm text-gray-600">Update your password to keep your account secure.</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Current Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={passwordData.current}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, current: e.target.value }))}
                  className="input-field pr-12"
                  placeholder="Enter your current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-500 transition-colors hover:bg-gray-100"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">New Password</label>
                <input
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, new: e.target.value }))}
                  className="input-field"
                  placeholder="Enter new password"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData((prev) => ({ ...prev, confirm: e.target.value }))}
                  className="input-field"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row">
              <button
                onClick={handlePasswordChange}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-gray-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-gray-800"
              >
                <Lock className="h-4 w-4" />
                Update Password
              </button>
              <button
                onClick={handleSaveSettings}
                className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-orange-600 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-orange-700"
              >
                <Save className="h-4 w-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
